import React from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { GraduationCap, Clock, ArrowRight } from "lucide-react";

interface LearningSpotlightSectionProps {
  items: any[];
  isLoading: boolean;
}

export function LearningSpotlightSection({ items, isLoading }: LearningSpotlightSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby="learning-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Learning"
          icon={GraduationCap}
          title="Learning Spotlight"
          href="/learning"
        />
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item: any) => (
              <Card key={item.id} className="group hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <GraduationCap className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-2">
                    {item.description ?? ""}
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{item.lessonCount ?? 0} lessons</span>
                    <span className="text-neutral-300">·</span>
                    <span>{item.duration ?? "1 hr"}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={GraduationCap} description="No learning paths yet." />
        )}
      </div>
    </section>
  );
}